'use client';

import {
  SiteHeader as ChromeSiteHeader,
  type HeaderNavItem,
} from '@/components/chrome/site-header';
import { CutMenu } from '@/components/navigation/cut-menu';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { SearchButton } from '@/components/search';
import { ThemeAwareLogo } from '@/components/common';
import { NAVIGATION, BLOG_NAV, WORK_NAV } from '@/lib/navigation';
import { trackCutChoice, trackThemeToggle } from '@/lib/analytics';
import type { ThemeCut } from '@/lib/theme-cuts';

/**
 * Site Header — thin wrapper around the registry-installed chrome header.
 *
 * Site config (nav items, dropdown shapes, logo, search, analytics) lives
 * here; all rendering/behavior lives in @/components/chrome/site-header.
 */
const HEADER_LINKS: HeaderNavItem[] = NAVIGATION.header.map((item) => {
  if (item.label === 'Blog') {
    return {
      ...item,
      dropdown: {
        ariaLabel: 'Blog menu',
        activePrefix: '/blog',
        items: BLOG_NAV,
        dividerAfterIndex: 1,
      },
    };
  }
  if (item.label === 'Work') {
    return {
      ...item,
      dropdown: {
        ariaLabel: 'Our Work menu',
        activePrefix: '/work',
        items: WORK_NAV,
        dividerAfterIndex: WORK_NAV.length - 4,
      },
    };
  }
  return item;
});

export interface SiteHeaderProps {
  /**
   * The cut the server stamped on <html> from the dcyfr-cut cookie, or
   * undefined for the base cut. Seeds the cut menu so the button names the
   * served cut and the hydrated markup matches the server's.
   */
  cut?: ThemeCut;
}

export function SiteHeader({ cut }: SiteHeaderProps = {}) {
  const actions = (
    <>
      <SearchButton variant="default" />
      {/* Mobile keeps Contact in the drawer; the CTA appears from sm up */}
      <Button asChild size="sm" className="hidden sm:inline-flex">
        <Link href={NAVIGATION.cta.href} title={NAVIGATION.cta.description}>
          {NAVIGATION.cta.label}
        </Link>
      </Button>
      {/*
        The visitor's cut control, last in the slot so it sits beside the
        theme toggle: a disclosure button that opens the vendored cut
        chooser in a panel (cut-menu.tsx says why the chooser is not inline:
        the centred desktop nav leaves no room for five named options). The
        chrome header renders `actions` in the phone header row as well,
        with whichever row the viewport does not show display:none and
        inert, so this one instance is the phone's chooser too: compact
        below md (swatch and chevron, the cut's name in the accessible name
        only) so the phone row still fits search, the toggle and the
        hamburger at 320px. Phones get it here rather than in the drawer or
        the bottom bar because both of those are registry chrome shared by
        every chrome-parity site, and a slot there would move all of them.
        The chooser writes the cookie and restamps <html data-cut> itself;
        the server stamp on the next request is what makes the choice
        stick.
      */}
      <CutMenu cut={cut} onChange={trackCutChoice} compact="max-md" />
    </>
  );

  return (
    <>
      <ChromeSiteHeader
        logo={
          <ThemeAwareLogo
            width={28}
            height={28}
            className="w-[clamp(1.75rem,2vw+1rem,1.75rem)] h-[clamp(1.75rem,2vw+1rem,1.75rem)] rounded-full"
          />
        }
        logoAriaLabel="DCYFR Labs Home"
        links={HEADER_LINKS}
        actions={actions}
        mobileNavSections={NAVIGATION.mobile}
        onThemeChange={trackThemeToggle}
      />
      {/*
       * The chrome header is position:fixed (so it can hide on scroll down and
       * reappear on scroll up). This in-flow spacer reserves its exact height
       * (h-18 + 1px bottom border) so page content starts below the header
       * instead of underneath it.
       */}
      <div aria-hidden="true" className="h-18 box-content border-b border-transparent" />
    </>
  );
}
